import { ApiError, type ApiErrorBody, type ApiErrorCode, type StoredFile } from '@tnn/core'

/** All API calls go through `/api` on the app's own origin (proxied to the API service). */
export const API_BASE = '/api'

const codeByStatus: Record<number, ApiErrorCode> = {
  400: 'BAD_REQUEST',
  401: 'UNAUTHORIZED',
  403: 'FORBIDDEN',
  404: 'NOT_FOUND',
  409: 'CONFLICT',
  413: 'BAD_REQUEST',
  415: 'BAD_REQUEST',
  422: 'VALIDATION',
}

type Listener = (error: ApiError) => void
const unauthorizedListeners = new Set<Listener>()

/** What makes the app ask who is signed in again: signed out (401), or the session waiting for its PIN */
const signInAgain = new Set<ApiErrorCode>(['UNAUTHORIZED', 'PIN_SETUP', 'PIN_LOCKED'])

let askedAt = 0

/** When the API was last asked anything from this page (ms): a session left without a request locks (`usePinWatch`) */
export const lastRequestAt = () => askedAt

/**
 * Called when any request comes back 401, or the session needs its PIN (none set yet, or idle too long), so the app
 * shows sign-in or the PIN
 */
export function onUnauthorized(listener: Listener): () => void {
  unauthorizedListeners.add(listener)
  return () => unauthorizedListeners.delete(listener)
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response
  try {
    // A FormData body (file upload) sets its own multipart content type
    const form = body instanceof FormData
    askedAt = Date.now()
    res = await fetch(`${API_BASE}${path}`, {
      method,
      credentials: 'include',
      headers: body === undefined || form ? undefined : { 'content-type': 'application/json' },
      body: body === undefined ? undefined : form ? body : JSON.stringify(body),
    })
  } catch {
    throw new ApiError('INTERNAL', 'network')
  }
  if (res.status === 204) return undefined as T
  const data = (await res.json().catch(() => null)) as unknown
  if (!res.ok) {
    const err = (data as ApiErrorBody | null)?.error
    const error = new ApiError(err?.code ?? codeByStatus[res.status] ?? 'INTERNAL', err?.message ?? res.statusText, err?.issues)
    if (res.status === 401 || signInAgain.has(error.code)) for (const l of unauthorizedListeners) l(error)
    throw error
  }
  return data as T
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body ?? {}),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body ?? {}),
  delete: <T = void>(path: string, body?: unknown) => request<T>('DELETE', path, body),
  /** Uploads one file (`POST /api/files`, or a portable screen's `/screen/files`): its id goes into a `file` field */
  upload: (file: File, path = '/files') => {
    const body = new FormData()
    body.append('file', file)
    return request<StoredFile>('POST', path, body)
  },
}

export function isApiError(error: unknown, code?: ApiErrorCode): error is ApiError {
  return error instanceof ApiError && (code === undefined || error.code === code)
}

/** Network failures and server errors (an API restart, a deploy) are worth retrying; 4xx answers are not. */
export function isTransient(error: unknown): boolean {
  return !isApiError(error) || error.code === 'INTERNAL'
}
